import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  Suspense,
} from 'react'
import { AppDispatch } from '../../../redux/store'
import { useDispatch } from 'react-redux'
import {} from '../../../redux/reducers/documents/contracts'
import { fetchClients } from '../../../redux/reducers/clients'
import ContentDeliveries from './ContentDeliveries'
import {
  fetchCargos,
  setIsOnlyCargo,
  setIsVisibleModalCargo,
  setVisibleContentDeliveries,
  setVisibleSideModal,
} from '../../../redux/reducers/cargos'
import classNames from 'classnames'
import styles from './Accounts.module.css'
import { Invoice } from '../../../redux/reducers/invoices'
import { fetchAllReports } from '../../../redux/reducers/reports'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import InvoicesMobileButtonsPanel from './InvoicesMobileButtonsPanel'
import { getRole } from '../../../utils/getRole'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { useSelector } from 'react-redux'
import LoadingPage from '../../../components/loading/LoadingPage'

// Lazy loading компонентов
const FilterInvoices = React.lazy(() => import('./FilterInvoices'))
const ContentInvoices = React.lazy(() => import('./ContentInvoices'))

type Currency = 'RUB' | 'BYN' | 'EUR' | 'USD'
const { isClient } = getRole()

const Accounts = React.memo(function Accounts() {
  const dispatch = useDispatch<AppDispatch>()
  const { cargos: cargosData } = useSelector(cargosSelector)

  const [updatedInvoices, setUpdatedInvoices] = useState<[] | Invoice[]>([])
  const [isMobileButtonsFixed, setIsMobileButtonsFixed] = useState(false)
  const [debouncedIsFixed, setDebouncedIsFixed] = useState(false)

  const currentYear = useMemo(() => new Date().getFullYear(), [])

  // Итого к оплате: сумма грузов без счёта (invoice_id === null), по валютам
  const totalToPayFromCargos = useMemo(() => {
    const list = Object.values(cargosData || {}).flat()
    const onlyUnpaid = list.filter(
      (c: { invoice_id?: number | null }) => c.invoice_id == null
    )
    const sums: Record<string, number> = {}
    onlyUnpaid.forEach((c: { amount: number; currency?: string }) => {
      const cur = c.currency || 'BYN'
      sums[cur] = (sums[cur] || 0) + Number(c.amount)
    })
    return Object.entries(sums)
      .filter(([, amount]) => amount > 0)
      .map(([currency, amount]) => ({ currency, amount }))
  }, [cargosData])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedIsFixed(isMobileButtonsFixed)
    }, 100)

    return () => clearTimeout(timer)
  }, [isMobileButtonsFixed])

  const calculateSumsByCurrency = useCallback((invoices: Invoice[]) => {
    const sums = {
      paid: {
        RUB: 0,
        BYN: 0,
        EUR: 0,
        USD: 0,
      } as Record<Currency, number>,
      unpaid: {
        RUB: 0,
        BYN: 0,
        EUR: 0,
        USD: 0,
      } as Record<Currency, number>,
      onPayment: {
        RUB: 0,
        BYN: 0,
        EUR: 0,
        USD: 0,
      } as Record<Currency, number>,
    }

    invoices.forEach((invoice) => {
      const currency = invoice.client.currency as Currency
      const amount = invoice.sum

      if (invoice.paid === 1) {
        sums.paid[currency] += amount
      } else if (invoice.paid === 2) {
        sums.onPayment[currency] += amount
      } else {
        sums.unpaid[currency] += amount
      }
    })

    return sums
  }, [])

  const formatAmountWithCurrency = useCallback(
    (amount: number, currency: string) => {
      if (amount === 0) return null
      return ` ${getAmount(amount)} ${getItemCurrency(currency)}`
    },
    []
  )

  // Мемоизируем вычисление сумм - тяжелая операция
  const sums = useMemo(
    () => calculateSumsByCurrency(updatedInvoices),
    [updatedInvoices, calculateSumsByCurrency]
  )

  // Мемоизируем отформатированные суммы для каждого типа оплаты
  const paidSums = useMemo(() => {
    return Object.entries(sums.paid)
      .filter(([_, amount]) => amount > 0)
      .map(([currency, amount]) => ({
        currency,
        amount,
        formatted: formatAmountWithCurrency(amount, currency),
      }))
  }, [sums.paid, formatAmountWithCurrency])

  const unpaidSums = useMemo(() => {
    return Object.entries(sums.unpaid)
      .filter(([_, amount]) => amount > 0)
      .map(([currency, amount]) => ({
        currency,
        amount,
        formatted: formatAmountWithCurrency(amount, currency),
      }))
  }, [sums.unpaid, formatAmountWithCurrency])

  const onPaymentSums = useMemo(() => {
    return Object.entries(sums.onPayment)
      .filter(([_, amount]) => amount > 0)
      .map(([currency, amount]) => ({
        currency,
        amount,
        formatted: formatAmountWithCurrency(amount, currency),
      }))
  }, [sums.onPayment, formatAmountWithCurrency])

  const handleClickOpenSideModal = useCallback(() => {
    // Открываем боковую панель и показываем только первый слайд с ContentDeliveries
    dispatch(setVisibleSideModal(true))
    dispatch(setVisibleContentDeliveries(false)) // НЕ показываем второй слайд сразу
  }, [dispatch])

  const handleOpenCargoModal = useCallback(() => {
    dispatch(setIsVisibleModalCargo(true))
    dispatch(setVisibleSideModal(false))
  }, [dispatch])

  const handleOpenCargoModalDesktop = useCallback(() => {
    dispatch(setIsOnlyCargo(true))
    dispatch(setVisibleSideModal(false))
    dispatch(setVisibleContentDeliveries(false))
    dispatch(setIsVisibleModalCargo(true))
  }, [dispatch])

  // Инициализация данных при монтировании компонента
  useEffect(() => {
    if (!isClient) {
      dispatch(fetchClients())
      dispatch(fetchAllReports())
    }
    dispatch(fetchCargos())
  }, [dispatch, currentYear])

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      dispatch(setVisibleContentDeliveries(false))
      dispatch(setVisibleSideModal(false))
    }
  }, [dispatch])

  // Добавляем/убираем класс на body для отступа при фиксированных кнопках
  useEffect(() => {
    if (debouncedIsFixed) {
      document.body.classList.add('buttons-fixed')
    } else {
      document.body.classList.remove('buttons-fixed')
    }

    return () => {
      document.body.classList.remove('buttons-fixed')
    }
  }, [debouncedIsFixed])

  return (
    <div className="tab-content" data-content="tab_docs_2">
      <div className="row">
        <div className="d-none d-md-block col-sm-4 col-md-4 col-lg-4 col-xl-4 pr-4">
          <div className="row mb-2">
            <div className="col">
              <div className={classNames(styles.h4, 'mb-2')}>К&nbsp;оплате</div>
            </div>
            {!isClient && (
              <div className="col-auto" style={{ textAlign: 'right' }}>
                <div
                  onClick={handleOpenCargoModalDesktop}
                  className={classNames(
                    styles.btn,
                    styles.btnSmall2,
                    styles.btnWide
                  )}
                >
                  + Груз
                </div>
              </div>
            )}
          </div>
          {totalToPayFromCargos.length > 0 && (
            <div
              className={classNames(
                styles.small2,
                styles.totalToPayWrap,
                'mb-2'
              )}
            >
              {totalToPayFromCargos.map(({ currency, amount }) => (
                <span
                  key={currency}
                  className={classNames(styles.totalToPayBadge, styles.small3)}
                >
                  <b>
                    {getAmount(amount)}
                    &nbsp;{getItemCurrency(currency)}
                  </b>
                </span>
              ))}
            </div>
          )}
          <ContentDeliveries />
        </div>
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-8">
          <div className="row">
            <div className="col-auto mb-2">
              <div className={classNames(styles.h4, 'mb-0')}>Счета</div>
            </div>

            <div
              className={classNames(styles.small2, 'col mb-2')}
              style={{ textAlign: 'right' }}
            >
              {paidSums.length > 0 && (
                <>
                  Оплачено:{' '}
                  {paidSums.map(({ currency, formatted }, index, array) => (
                    <React.Fragment key={currency}>
                      <b className={styles.colorGreen}>{formatted}</b>
                      {index < array.length - 1 && ' / '}
                    </React.Fragment>
                  ))}
                </>
              )}

              {(paidSums.length > 0 || onPaymentSums.length > 0) &&
                unpaidSums.length > 0 && <br />}
              {unpaidSums.length > 0 && (
                <>
                  К оплате:{' '}
                  {unpaidSums.map(({ currency, formatted }, index, array) => (
                    <React.Fragment key={currency}>
                      <b className={styles.colorRed}>{formatted}</b>
                      {index < array.length - 1 && ' / '}
                    </React.Fragment>
                  ))}
                </>
              )}

              {paidSums.length > 0 &&
                (onPaymentSums.length > 0 || unpaidSums.length > 0) && <br />}
              {onPaymentSums.length > 0 && (
                <>
                  На оплате:{' '}
                  {onPaymentSums.map(
                    ({ currency, formatted }, index, array) => (
                      <React.Fragment key={currency}>
                        <b className={styles.colorTurquoise}>{formatted}</b>
                        {index < array.length - 1 && ' / '}
                      </React.Fragment>
                    )
                  )}
                </>
              )}
            </div>
          </div>

          <InvoicesMobileButtonsPanel
            isFixed={isMobileButtonsFixed}
            onOpenPanel={handleClickOpenSideModal}
            onOpenModal={handleOpenCargoModal}
          />
          <Suspense fallback={<LoadingPage />}>
            <FilterInvoices
              setUpdatedInvoices={setUpdatedInvoices}
              onFilterFixedChange={setIsMobileButtonsFixed}
              isFixed={isMobileButtonsFixed}
            />
          </Suspense>
          <Suspense fallback={<LoadingPage />}>
            <ContentInvoices
              updatedInvoices={updatedInvoices}
              isFixed={isMobileButtonsFixed}
            />
          </Suspense>
          {/* <InvoicesMobileButtonsPanel
            isFixed={isMobileButtonsFixed}
            onOpenPanel={handleClickOpenSideModal}
            onOpenModal={handleOpenCargoModal}
          /> */}
        </div>
      </div>
    </div>
  )
})

Accounts.displayName = 'Accounts'

export default Accounts
